import React from 'react';
import { Box, Stack, Typography, Button, IconButton, Tooltip } from '@mui/material';
import { UploadFile, Close, CheckCircle } from '@mui/icons-material';

import type { DocumentChecklistItem, DocumentStanding } from '../../../types/admissions';
import { ToneChip, ChipTone } from '../../common/StatusChip';

/**
 * The cycle's document checklist as an upload list — the one way documents
 * are asked for, used on the public form, the parent's status page and the
 * walk-in dialog. Each row: the document, whether it's required, the file
 * chosen (or where an uploaded one stands), and a picker limited to the
 * file types the school allows.
 */

export const MAX_DOCUMENT_BYTES = 5 * 1024 * 1024;

const ACCEPT: Record<string, string> = {
  pdf: '.pdf,application/pdf',
  jpg: '.jpg,.jpeg,image/jpeg',
  jpeg: '.jpg,.jpeg,image/jpeg',
  png: '.png,image/png',
};
const acceptFor = (types: string[]) =>
  (types.length ? types : ['pdf', 'jpg', 'png']).map(t => ACCEPT[t.toLowerCase()] ?? `.${t}`).join(',');

const STANDING: Record<string, { label: string; tone: ChipTone }> = {
  missing:  { label: 'Not uploaded', tone: 'neutral' },
  pending:  { label: 'Received', tone: 'info' },
  uploaded: { label: 'Received', tone: 'info' },
  verified: { label: 'Verified', tone: 'success' },
  rejected: { label: 'Please upload again', tone: 'error' },
};

/** Why a chosen file can't be used, or null. Mirrors the server's rules. */
export function documentFileProblem(file: File, item: Pick<DocumentChecklistItem, 'label' | 'fileTypes'>): string | null {
  if (file.size > MAX_DOCUMENT_BYTES) return `${item.label} must be under 5 MB.`;
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  const allowed = (item.fileTypes || []).map(t => t.toLowerCase());
  const ok = !allowed.length || allowed.includes(ext) || (['jpg', 'jpeg'].includes(ext) && allowed.some(t => t === 'jpg' || t === 'jpeg'));
  return ok ? null : `${item.label} must be a ${allowed.map(t => t.toUpperCase()).join(', ')} file.`;
}

type Row = Pick<DocumentChecklistItem, 'key' | 'label' | 'required' | 'fileTypes'> & Partial<Pick<DocumentStanding, 'status' | 'fileName' | 'rejectedReason'>>;

interface Props {
  items: Row[];
  /** Files chosen but not uploaded yet (forms upload after submitting). */
  picked?: Record<string, File | null | undefined>;
  onPick: (key: string, file: File | null) => void;
  errors?: Record<string, string | undefined>;
  /** Row currently uploading (status page uploads immediately). */
  busyKey?: string | null;
  disabled?: boolean;
}

const DocumentChecklistInput: React.FC<Props> = ({ items, picked = {}, onPick, errors = {}, busyKey, disabled }) => (
  <Stack spacing={1.25}>
    {items.map(item => {
      const file = picked[item.key];
      const standing = item.status ? STANDING[item.status] : null;
      const locked = item.status === 'verified';
      const busy = busyKey === item.key;
      return (
        <Box
          key={item.key}
          data-field-key={`doc:${item.key}`}
          sx={{
            p: 1.75, borderRadius: '12px', border: '1px solid',
            borderColor: errors[item.key] ? 'error.main' : file || locked ? 'success.light' : 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="body2" fontWeight={600}>{item.label}</Typography>
                <Typography variant="caption" color="text.secondary">{item.required ? 'Required' : 'Optional'}</Typography>
                {standing && !file && <ToneChip label={standing.label} tone={standing.tone} />}
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                {file
                  ? <><CheckCircle sx={{ fontSize: 13, color: 'success.main', verticalAlign: 'text-bottom', mr: 0.5 }} />{file.name}</>
                  : item.fileName && item.status !== 'rejected'
                    ? item.fileName
                    : `${(item.fileTypes?.length ? item.fileTypes : ['pdf', 'jpg', 'png']).map(t => t.toUpperCase()).join(', ')} · up to 5 MB`}
              </Typography>
              {item.status === 'rejected' && item.rejectedReason && !file && (
                <Typography variant="caption" color="error.main" sx={{ display: 'block' }}>{item.rejectedReason}</Typography>
              )}
              {errors[item.key] && (
                <Typography variant="caption" color="error.main" sx={{ display: 'block' }}>{errors[item.key]}</Typography>
              )}
            </Box>
            {!locked && (
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
                {file && (
                  <Tooltip title="Remove">
                    <IconButton size="small" onClick={() => onPick(item.key, null)} disabled={disabled} aria-label={`Remove ${item.label}`}>
                      <Close fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                <Button
                  component="label"
                  size="small"
                  variant={file || item.fileName ? 'text' : 'outlined'}
                  startIcon={<UploadFile fontSize="small" />}
                  disabled={disabled || busy}
                  sx={{ textTransform: 'none' }}
                >
                  {busy ? 'Uploading…' : file || (item.fileName && item.status !== 'rejected') ? 'Replace' : 'Choose file'}
                  <input
                    hidden
                    type="file"
                    accept={acceptFor(item.fileTypes || [])}
                    aria-label={`Upload ${item.label}`}
                    onChange={(e) => {
                      const f = e.target.files?.[0] ?? null;
                      e.target.value = '';
                      if (f) onPick(item.key, f);
                    }}
                  />
                </Button>
              </Stack>
            )}
          </Stack>
        </Box>
      );
    })}
  </Stack>
);

export default DocumentChecklistInput;
